import React from 'react';
import { Grid, Typography, CardContent, Box, Chip, Divider, Stack } from '@mui/material';
import { StyledCard, ImageWithPreview } from './shared';
import { getHTTPUrlFromS3Path } from '../../../utils/outputs';
import moment from 'moment';
import { ScanDetails } from 'types/scan';

interface ProgressPair {
  image1: string;
  image2: string;
  caption: string;
}

interface ProgressSummary {
  text: string;
  floorplan_id: string;
}

interface ProgressValue {
  stage: string;
  pairs: ProgressPair[];
  summary: ProgressSummary[];
  extensions: Record<string, unknown>;
}

interface ProgressSectionProps {
  progress: Record<string, ProgressValue>;
  scans: ScanDetails[];
  selectedScan: string;
  analyticsCreatedAt?: string;
  selectedScanData?: ScanDetails;
}

const ProgressSection: React.FC<ProgressSectionProps> = ({ 
  progress, 
  scans, 
  selectedScan,
  analyticsCreatedAt,
  selectedScanData
}) => {
  if (!progress || Object.keys(progress).length === 0) return null;

  return (
    <Grid item xs={12}>
      <Grid container spacing={3}>
        {Object.entries(progress).map(([comparedScanId, value]) => {
          // Find the compared scan in the already fetched scans list
          const comparedScan = scans.find(scan => scan.id === comparedScanId);
          // For the detailed scan info, we'll use what we have from the scans list
          const comparedScanDetails = comparedScan;
          
          // Calculate time difference between scans
          const comparedScanDate = comparedScan 
            ? new Date(comparedScan.scanDate || comparedScan.createdAt || Date.now())
            : new Date();
          const currentScanDate = analyticsCreatedAt 
            ? new Date(analyticsCreatedAt) 
            : new Date();
          const daysDiff = Math.round((currentScanDate.getTime() - comparedScanDate.getTime()) / (1000 * 60 * 60 * 24));
          
          return (
            <Grid item xs={12} key={comparedScanId}>
              <StyledCard>
                <CardContent>
                  {/* Header with scan information */}
                  <Box sx={{ mb: 3, p: 2, borderRadius: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" fontWeight="medium">
                            {value.stage}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {/* Scan comparison details */}
                    <Grid container spacing={2} sx={{ position: 'relative' }}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight="medium">Before: Compared Scan</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              ID: {comparedScanId}
                            </Typography>
                            {(comparedScanDetails?.scanDate || comparedScan?.scanDate) && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Date: {moment(comparedScanDetails?.scanDate || comparedScan?.scanDate || comparedScan?.createdAt).format('MMM D, YYYY')}
                                {daysDiff > 0 && ` (${daysDiff} days earlier)`}
                              </Typography>
                            )}
                            {(comparedScanDetails?.description || comparedScan?.description) && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Description: {comparedScanDetails?.description || comparedScan?.description}
                              </Typography>
                            )}
                            {comparedScan?.status && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Status: {comparedScan.status}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Divider orientation="vertical" flexItem sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, transform: 'translateX(-50%)', my: 2 }} />
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, border: 'none', bgColor: '#fff1', borderRadius: 1, height: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight="medium">After: Current Scan</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              ID: {selectedScan}
                            </Typography>
                            {analyticsCreatedAt && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Date: {moment(analyticsCreatedAt).format('MMM D, YYYY')}
                              </Typography>
                            )}
                            {selectedScanData?.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Description: {selectedScanData.description}
                              </Typography>
                            )}
                            {selectedScanData?.status && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Status: {selectedScanData.status}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {/* Summary section */}
                  {value.summary?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.700', mr: 1.5 }} />
                        Summary
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        {value.summary.map((summaryItem, summaryIndex) => (
                          <Typography key={summaryIndex} variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ mr: 1.5, mt: 0.5, width: 4, height: 4, borderRadius: '50%', bgcolor: 'grey.500', flexShrink: 0 }} />
                            <Box component="span">{summaryItem.text}</Box>
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {/* Image comparisons */}
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.700', mr: 1.5 }} />
                    Image Comparisons
                  </Typography>
                  <Grid container spacing={3}>
                    {value.pairs.map((pair, pairIndex) => (
                      <Grid item xs={12} key={pairIndex}>
                        <Box sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                          <Stack direction="row" alignItems="center" gap={.5} sx={{ mb: 2 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.700', mr: 1.5 }} />
                            <Typography variant="body1" sx={{ borderColor: 'grey.300', fontWeight: 'medium' }}>
                              {pair.caption}
                            </Typography>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: 0, left: 0, bgcolor: '#ffffff', color: '#000000', px: 1, py: 0.5, fontSize: '0.75rem', fontWeight: 'medium', zIndex: 1, borderBottomRightRadius: 4 }}>
                                  Before
                                </Box>
                                <ImageWithPreview
                                  src={(url => url.endsWith('/') ? url.slice(0, -1) : url)(getHTTPUrlFromS3Path(pair.image1))}
                                  alt={`Previous - ${pair.caption}`}
                                  identifier={`progress-${comparedScanId}-${pairIndex}-before`}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: 0, left: 0, bgcolor: '#ffffff', color: '#000000', px: 1, py: 0.5, fontSize: '0.75rem', fontWeight: 'medium', zIndex: 1, borderBottomRightRadius: 4 }}>
                                  After
                                </Box>
                                <ImageWithPreview
                                  src={(url => url.endsWith('/') ? url.slice(0, -1) : url)(getHTTPUrlFromS3Path(pair.image2))}
                                  alt={`Current - ${pair.caption}`}
                                  identifier={`progress-${comparedScanId}-${pairIndex}-after`}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default ProgressSection;
